import { CmsMeta } from '../cmsMeta';
import { UiContainer } from '../../../state/ui';
import { close, Icon } from '../../Icon/Icon';
import SanitizedHtml from '../../SanitizedHtml/SanitizedHtml';
import './CmsTopBanner.css';

type CmsTopBannerProps = {
	content: string;
	mobileContent?: string;
	backgroundColor: string;
	closable?: boolean;
	image?: string;
	iconAlt?: string;
	contentHref?: string;
	contentTarget?: string;
	linkText?: string;
};

export const CmsTopBanner = ({
	content,
	mobileContent,
	backgroundColor,
	closable = true,
	image,
	iconAlt = 'Banner icon',
	contentHref,
	contentTarget = '_self',
	linkText,
}: CmsTopBannerProps) => {
	const { isTopBannerOpen, setTopBannerOpen } = UiContainer.useContainer();
	if (!isTopBannerOpen || !content) return null;

	const desktopHtml = content;
	const mobileHtml = mobileContent ?? content;

	const innerContent = (
		<>
			<SanitizedHtml className="cms-top-banner__content cms-top-banner__content--desktop" html={desktopHtml} />
			<SanitizedHtml className="cms-top-banner__content cms-top-banner__content--mobile" html={mobileHtml} />
			{image && <img className="cms-top-banner__icon" src={image} alt={iconAlt} />}
			{contentHref && linkText && <span className="cms-top-banner__link-text">{linkText}</span>}
		</>
	);

	const Container = ({ children }: { children: React.ReactNode }) => {
		if (contentHref) {
			return (
				<a
					className="cms-top-banner__link"
					href={contentHref}
					target={contentTarget}
					rel={contentTarget === '_self' ? undefined : 'noreferrer noopener'}
				>
					{children}
				</a>
			);
		}
		return <div>{children}</div>;
	};

	return (
		<div className="cms-top-banner">
			<div className="cms-top-banner__bar" style={{ backgroundColor }}>
				{contentHref ? <Container>{innerContent}</Container> : innerContent}
				{closable && (
					<button
						aria-label="Dismiss top banner"
						className="cms-top-banner__close"
						onClick={() => setTopBannerOpen(false)}
					>
						<Icon shape={close} size={20} />
					</button>
				)}
			</div>
		</div>
	);
};

export const cmsMeta: CmsMeta = {
	description:
		'The strip across the very top of every page. One short sentence - it is the first thing read and the easiest to leave up too long.',
	example: [
		'CmsTopBanner',
		{
			content: 'Worky er blevet til Ondio 🎉',
			mobileContent: 'Worky er blevet til Ondio 🎉',
			backgroundColor: '#78E989',
			contentHref: '/navneskift',
			contentTarget: '_self',
			closable: true,
		},
	],
};
